/* RESPONSIBLE TEAM: team-proactive-support */
import { tracked } from '@glimmer/tracking';
import type Graph from './graph';
import { type Alignment } from './connection-point';

interface CoordinateLike {
  asCoordinates(): Coordinates;
  alignment: Alignment;
}

export { CoordinateLike };

export default class Coordinates implements CoordinateLike {
  @tracked x: number;
  @tracked y: number;
  alignment: Alignment = 'no-alignment';

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  static fromJSON(inputs: any): Coordinates {
    let { x, y } = inputs;
    return new Coordinates(Number(x), Number(y));
  }

  asCoordinates() {
    return this;
  }

  // √(x2 - x1)² + (y2 - y1)²
  distanceTo(otherCoordinates: Coordinates): number {
    return Math.sqrt(
      Math.pow(otherCoordinates.x - this.x, 2) + Math.pow(otherCoordinates.y - this.y, 2),
    );
  }

  // Like distanceTo, but without the square root.
  proportionalDistanceTo(otherCoordinates: Coordinates): number {
    return Math.pow(otherCoordinates.x - this.x, 2) + Math.pow(otherCoordinates.y - this.y, 2);
  }

  static closestCoordinates(
    from: Array<CoordinateLike>,
    to: Array<CoordinateLike>,
  ): [CoordinateLike, CoordinateLike] {
    return this.coordinatePairsByDistance(from, to)[0];
  }

  static coordinatePairsByDistance(
    from: Array<CoordinateLike>,
    to: Array<CoordinateLike>,
  ): Array<[CoordinateLike, CoordinateLike]> {
    let coordinatesByDistance: Array<{
      distance: number;
      fromCoordinates: CoordinateLike;
      toCoordinates: CoordinateLike;
    }> = [];

    from.forEach((fromCoordinates) => {
      to.forEach((toCoordinates) => {
        let distance = fromCoordinates
          .asCoordinates()
          .proportionalDistanceTo(toCoordinates.asCoordinates());
        coordinatesByDistance.push({ distance, fromCoordinates, toCoordinates });
      });
    });

    return coordinatesByDistance
      .sort((a, b) => a.distance - b.distance)
      .map((coordinateByDistance) => [
        coordinateByDistance.fromCoordinates,
        coordinateByDistance.toCoordinates,
      ]);
  }

  static roundedCoordinate(coordinate: number, spacing: number): number {
    return Math.round(coordinate / spacing) * spacing;
  }

  private static scaledCoordinate(coordinate: number, scale: number): number {
    return coordinate / scale;
  }

  static scaledAndRounded(x: number, y: number, graph: Graph): Coordinates {
    let offset = graph.state.cursorOffset;
    let bounds = graph.elementRect;
    let scale = graph.state.scale;
    let spacing = graph.settings.grid.spacing;

    let scaledX = this.scaledCoordinate(x - bounds.x, scale) - offset.x;
    let scaledY = this.scaledCoordinate(y - bounds.y, scale) - offset.y;

    let scaledAndRoundedX = this.roundedCoordinate(scaledX, spacing);
    let scaledAndRoundedY = this.roundedCoordinate(scaledY, spacing);

    return new Coordinates(scaledAndRoundedX, scaledAndRoundedY);
  }

  static scaled(x: number, y: number, graph: Graph): Coordinates {
    let bounds = graph.elementRect;

    return new Coordinates(
      Math.round((x - bounds.x) / graph.state.scale),
      Math.round((y - bounds.y) / graph.state.scale),
    );
  }
}
